/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import _ from "lodash"
import moment from "moment"
import numeral from "numeral"
import {
  ActivityIndicator,
  Appearance,
  ListRenderItem,
  ListRenderItemInfo,
  SectionListData,
  TouchableOpacity,
  useWindowDimensions,
  ViewStyle,
  ImageStyle
} from "react-native"
import {
  Cell,
  FloatButton,
} from "../cycle-water-quality-multi-measurement-list/cycle-water-quality-multi-measurement-list-screen"
import { Divider, Icon, Modal, ScrollView, Skeleton, useDisclose, View } from "native-base"
import { Button, JalaIcon, JalaLoading, LazyRender, ListEmpty, Screen, SectionList, Text, AutoImage } from "../../../../components"
import { SafeAreaView } from "react-native-safe-area-context"
import { color, nativeBaseTheme, images } from "../../../../theme"
import { navigate } from "../../../../navigators"
import { observer } from "mobx-react-lite"
import { translate } from "../../../../i18n"
import { useStores } from "../../../../models"
import { delay } from "../../../../utils/delay"
import { useFocusEffect } from "@react-navigation/native"
import JalaAnalytic from "../../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const CONTENT_CONTAINER: ViewStyle = {
  // flexGrow: 1,
  paddingBottom: 68
}

const SCROLL_VIEW_DETAIL_ITEM: ViewStyle = {
  flex: 1,
  marginBottom: 8
}

const BUTTON_DETAIL_ITEM: ViewStyle = {
  width: 120,
  alignSelf: "center",
  borderRadius: 40
}

const EMPTY_NOTES: ImageStyle = {
  resizeMode: 'contain',
  width: 14,
  height: 12,
  alignSelf: 'center'
}

const TableHead: FC<{ colorScheme?: "light" | "dark" }> = observer(function TableHead(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;

  const renderPlaceholder = useCallback(() => {
    return <Skeleton.Text lines={1} paddingX="8px" paddingY="10px" />
  }, [])

  return <View
    borderWidth="1px"
    borderColor={`gray.${colorScheme === 'light' ? '200' : '800'}`}
    backgroundColor={`gray.${colorScheme === 'light' ? '100' : '900'}`}
    flexDirection="row"
  >
    <LazyRender renderPlaceholder={renderPlaceholder}>
      <Cell text={_.upperFirst(translate('treatments.name'))} flex={3} textPreset="body3" colorScheme={colorScheme} />
      <Cell text={_.upperFirst(translate('treatments.quantity'))} textPreset="body3" colorScheme={colorScheme} />
      <Cell text={_.upperFirst(translate('treatments.remark'))} width="64px" textPreset="body3" colorScheme={colorScheme} />
      <Cell text={_.upperFirst(translate('edit'))} width="48px" padding="0px" alignItems="center" justifyContent="center" textPreset="body3" colorScheme={colorScheme} />
    </LazyRender>
  </View>;
});

const SectionHeader: FC<{ colorScheme?: "light" | "dark", info: { section: SectionListData<any, any> } }> = observer(function SectionHeader(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info } = props;
  const { section: { title: { age, treated_at } } } = info;

  const renderPlaceholder = useCallback(() => {
    return <View height="40px"><Skeleton.Text width="30%" lines={1} /></View>
  }, [])

  return <View height="40px" backgroundColor={colorScheme === "light" ? color.palette.white : nativeBaseTheme.colors.dark["50"]}>
    <View padding="8px">
      <LazyRender renderPlaceholder={renderPlaceholder}>
        <Text
          text={`(${age}) ${moment(treated_at).format('dddd, DD MMM YYYY')}`}
          preset="title4"
          colorScheme={colorScheme}
          color={`muted.${colorScheme === 'light' ? '700' : '300'}`}
        />
      </LazyRender>
    </View>
    <Divider />
  </View>
})

const DetailItemModal: FC<{ colorScheme?: "light" | "dark", item: any }> = observer(function DetailItemModal(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), item = {}, children } = props;
  const { isOpen, onClose, onOpen } = useDisclose()
  const { age, treated_at, remark } = item

  return (<>
    <TouchableOpacity onPress={onOpen}>
      {children}
    </TouchableOpacity>
    <Modal isOpen={isOpen} onClose={onClose}>
      <View
        flex={1}
        flexGrow={1}
        alignSelf="stretch"
        marginY="80px"
        marginX="16px"
        padding="16px"
        rounded="8px"
        backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
      >
        <Text tx="feeds.remark" transformText={_.upperFirst} preset="display2" colorScheme={colorScheme} marginBottom="8px" />
        <Text
          text={`${moment(treated_at).format('DD MMM YYYY')} (${age})`}
          preset="body2"
          colorScheme={colorScheme}
          marginBottom="8px"
        />
        <ScrollView
          contentContainerStyle={SCROLL_VIEW_DETAIL_ITEM}
        >
          <Text text={remark || '-'} preset="body2" colorScheme={colorScheme} />
        </ScrollView>
        <Button
          testID="jala:id/ok-button"
          tx="common.ok"
          preset="primary"
          colorScheme={colorScheme}
          style={BUTTON_DETAIL_ITEM}
          onPress={onClose}
        />
      </View>
    </Modal>
  </>)
})

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any>, handleEdit: (item: any) => void }> = observer(function Item(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info,
    handleEdit
  } = props;
  const {
    item,
    item: { name, quantity, remark, _pushing, is_can_be_pushed }
  } = info;

  const iconBaseStyle = nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme });

  const renderPlaceholder = useCallback(() => {
    return <View height="78px">
      <Skeleton.Text lines={1} padding="8px" _line={{ height: '14px' }} />
    </View>
  }, [])

  const onPressEdit = useCallback(() => handleEdit(item), [handleEdit, item])

  const valueFormatted = (value) => (
    value != null && numeral(value).format('0,0.[00]') || '-'
  )

  return <LazyRender renderPlaceholder={renderPlaceholder}>
    <View minHeight="56px" flexDirection="row" position="relative">
      <Cell flex={3} colorScheme={colorScheme} textColor="primary.700" alignItems="flex-start" paddingX="4px" textProps={{ isTruncated: false }}>
        <Text text={name} preset="body2" colorScheme={colorScheme} color="primary.700" />
        {remark && <Text text={remark} preset="body3" colorScheme={colorScheme} color={`muted.${colorScheme === 'light' ? '700' : '300'}`} />}
      </Cell>
      <Cell text={valueFormatted(quantity)} colorScheme={colorScheme} textColor="primary.700" />
      <Cell width="64px" paddingY="0px" justifyContent="center" colorScheme={colorScheme} alignItems="center">
        {remark ? <DetailItemModal colorScheme={colorScheme} item={item}>
          <Icon {...iconBaseStyle} as={JalaIcon} name="comment-1" size="18px" color="primary.500" />
        </DetailItemModal> : <AutoImage source={images.empty_notes} style={EMPTY_NOTES} />}
       </Cell>
      <Cell width="48px" paddingY="0px" justifyContent="center" alignItems="center" colorScheme={colorScheme}>
        {_pushing
          && <ActivityIndicator size={15} />
          || <TouchableOpacity onPress={onPressEdit}>
            <View size="32px" justifyContent="center" alignItems="center">
              <Icon as={JalaIcon} name="pencil" size="12px" color="primary.500"/>
            </View>
        </TouchableOpacity>}
      </Cell>
      {is_can_be_pushed && (
        <Cell
          width="10px" right="0px" top="5px" position="absolute" paddingY="0px"
          justifyContent="center" colorScheme={colorScheme} backgroundColor="blue.200"
        >
          <Icon {...iconBaseStyle} as={JalaIcon} name="cloud-upload" size="10px" color="primary.500" />
        </Cell>
       )}
    </View>
  </LazyRender>;
})

export const CycleTreatmentListScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function TreatmentTableScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  // const { someStore, anotherStore } = useStores()
  const {
    appStore,
    appStore: {
      isOffline,
      farmStore,
      farmStore: {
        pondStore: {
          cycleStore,
          cycleStore: {
            cycle,
            treatmentsPerDate: models,
            loadingTreatment: loadingModel
          }
        }
      }
    },
    treatmentStore: modelStore,
    treatmentStore: { pushIdQueue, needRefresh, storing, updating, deleting, modifying },
  } = useStores();

  const [dateTimeFilter, setDateTimeFilter] = useState<{
    date_after?: string,
    date_before?: string,
    time?: string
  }>({})

  const modelSections = useMemo(() => {
    return models.filter(({ treated_at }) => (
      (!dateTimeFilter.date_after || dateTimeFilter.date_after <= treated_at)
      && (!dateTimeFilter.date_before || treated_at <= dateTimeFilter.date_before)
    )).map(({ treated_at, age, treatments }) => ({
      title: { treated_at, age },
      data: _.orderBy(treatments, ['created_at'], ['desc']).map((m) => ({
        ...m,
        _pushing: !!pushIdQueue[m.id],
        _loading: loadingModel
      }))
    }))
  }, [dateTimeFilter, loadingModel, models, pushIdQueue])

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile app: pond - view tabs - treatment');
  }, []))

  useEffect(() => {
    modelStore.init();
    farmStore.getStocks(false).catch();
    cycleStore.getTreatments(!isOffline).catch();
  }, [farmStore, cycleStore, modelStore, isOffline])

  useEffect(() => {
    if (needRefresh) {
      if (isOffline) {
        cycleStore.getTreatments(false).then(() => {
          modelStore.set('needRefresh', null)
          modelStore.set('pushIdQueue', {})
        })
      } else {
        cycleStore.getTreatments(false).then(() => {
          cycleStore.getTreatments(true, () => {
            modelStore.set('needRefresh', null)
            modelStore.set('pushIdQueue', {})
          }).catch()
        })
      }
    }
  }, [cycleStore, isOffline, modelStore, needRefresh])

  const onAddPress = useCallback(async () => {
    JalaAnalytic.log('Mobile app: pond - add treatment');
    await delay(1);
    cycle && navigate('Home.Stack', 'Home.Treatment.Create', { pond_id: cycle.pond_id, cycle_id: cycle.id });
  }, [cycle])

  const handleEdit = useCallback((item) => {
    navigate('Home.Stack', 'Home.Treatment.Edit', { ...item })
  }, [])

  const renderSectionHeader = useCallback((info) => (
    <SectionHeader info={info} colorScheme={colorScheme} />
  ), [colorScheme])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} colorScheme={colorScheme} handleEdit={handleEdit} />
  }, [colorScheme, handleEdit])

  const keyExtractor = useCallback((item, index) => `${item.id || item.treated_at || index}`, [])

  const onRefresh = useCallback(() => {
    appStore.checkNetworkConnection().then(() => {
      farmStore.getStocks(!isOffline).catch()
      cycleStore.getTreatments(!isOffline).catch()
    }).catch()
  }, [appStore, cycleStore, farmStore, isOffline])

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      {!!modelSections.length && <TableHead colorScheme={colorScheme} />}
      <SectionList
        sections={modelSections}
        renderSectionHeader={renderSectionHeader}
        renderItem={renderItem}
        contentContainerStyle={CONTENT_CONTAINER}
        keyExtractor={keyExtractor}
        ListEmptyComponent={(loadingModel || modifying) && (
          <View flexGrow={1} alignItems="center" justifyContent="center" height={`${height - 214}px`}>
            <JalaLoading />
          </View>
        ) || <ListEmpty text={translate("listEmpty.text")} colorScheme={colorScheme} height={`${height - 214}px`} />}
        ListHeaderComponent={(loadingModel || storing) && !!modelSections.length && !updating && !deleting && (
          <View height="40px" alignItems="center" justifyContent="center" paddingTop="8px">
            <Text tx="fetchingData" preset="body2" colorScheme={colorScheme} color={nativeBaseTheme.colors.muted[colorScheme === 'light' ? '700' : '300']} />
          </View>
        ) || undefined}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        onRefresh={onRefresh}
        refreshing={false}
        estimatedItemSize={56}
      />
      {!cycle.finished_at && <FloatButton
        colorScheme={colorScheme}
        dateTimeFilterValue={dateTimeFilter}
        onDateTimeFilterChange={setDateTimeFilter}
        onAddPress={onAddPress}
      />}
    </Screen>
  )
})
